import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Checkbox,
  LinearProgress
} from '@mui/material';
import { Button } from '@atoms';
import { useBillingAccounts } from '@api';

type BusinessTypeFilter = 'all' | 'tourism' | 'estate' | 'mortgage' | 'other';

const BUSINESS_TYPE_OPTIONS: { value: BusinessTypeFilter; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'estate', label: 'Estate' },
  { value: 'tourism', label: 'Tourism' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'other', label: 'Other' }
];

const STATUS_COLORS: Record<string, { label: string; color: string; bg: string }> = {
  live: { label: 'Live', color: '#065F46', bg: '#D1FAE5' },
  staging: { label: 'Staging', color: '#92400E', bg: '#FEF3C7' },
  inactive: { label: 'Inactive', color: '#374151', bg: '#E5E7EB' }
};

const STATUS_ORDER: Record<string, number> = { live: 0, staging: 1, inactive: 2 };

/**
 * Account picker spanning Core (locationId-bearing) and/or Negotiator
 * (reapitCompanyId-bearing) billing accounts.
 *
 * - Filter by product with `type` (`'c'` = Core only, `'n'` = Negotiator only, omit for both).
 * - Filter by status with `status` (defaults to `'live'`).
 * - Set `multiple` for a multi-select variant (value/onSelected become array-typed).
 */
type CommonProps = {
  title?: string;
  status?: 'live' | 'staging' | 'both';
  /** Product filter passed to useBillingAccounts. `'c'` = Core, `'n'` = Negotiator. Omit for both. */
  type?: string;
  /** Unique key to isolate the React Query cache for this instance. */
  caller?: string;
  /** Optional override for the input width. */
  width?: number | string;
  required?: boolean;
  placeholder?: string;
  /**
   * Render mode:
   *   - `'search'` (default) — MUI Autocomplete with search-as-you-type.
   *   - `'checkbox-list'` — MUI Select dropdown with checkboxes and a Select-All row.
   *     Only valid together with `multiple`.
   */
  variant?: 'search' | 'checkbox-list';
  /** Render a business-type dropdown to the left of the input. */
  showBusinessTypeFilter?: boolean;
  /** Lock the visible options to a specific business type and hide the dropdown. */
  lockBusinessType?: BusinessTypeFilter;
  /** Fired whenever the business-type filter changes (only when the dropdown is shown). */
  onBusinessTypeChange?: (businessType: BusinessTypeFilter) => void;
  /**
   * `checkbox-list` + `multiple` only. When true, all loaded options are
   * pre-selected on first load — the classic "sync all" pattern.
   */
  defaultSelectAll?: boolean;
  /**
   * `multiple` only. One-shot pre-selection by locationId string(s).
   * Resolved to BillingAccount objects once the options load, then
   * hands ownership to internal state (uncontrolled semantic).
   */
  preSelectedLocationIds?: string[];
};

type SingleProps = {
  multiple?: false;
  value?: BillingAccount | null;
  onSelected?: (account: BillingAccount | null) => void;
  /**
   * Render a "Go" button to the right of the input once an account is
   * selected. The button always opens in a new tab (anchor + target=_blank),
   * so right-click, middle-click, and cmd/ctrl-click also work.
   */
  goHref?: (account: BillingAccount) => string;
};

type MultiProps = {
  multiple: true;
  value?: BillingAccount[];
  onSelected?: (accounts: BillingAccount[]) => void;
  goHref?: never;
};

type BillingAccountSelectorProps = CommonProps & (SingleProps | MultiProps);

export default function BillingAccountSelector(props: BillingAccountSelectorProps) {
  const {
    title = 'Search Billing Account',
    status = 'live',
    type,
    caller,
    width = 320,
    required,
    placeholder,
    variant = 'search',
    showBusinessTypeFilter = false,
    lockBusinessType,
    onBusinessTypeChange,
    defaultSelectAll,
    preSelectedLocationIds
  } = props;
  const isMultiple = props.multiple === true;
  const isCheckboxList = variant === 'checkbox-list';

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly BillingAccount[]>([]);
  const [businessTypeFilter, setBusinessTypeFilter] = React.useState<BusinessTypeFilter>(
    lockBusinessType ?? 'all'
  );
  const [selectedCount, setSelectedCount] = React.useState(0);
  // Track a local selection so the single-select "Go" button knows what to fire against.
  const [localSelected, setLocalSelected] = React.useState<BillingAccount | null>(null);
  // Uncontrolled fallback for the multi-select search variant. Without this,
  // callers that pass only `onSelected` (no `value`) get an Autocomplete stuck
  // at value=[] on every re-render — the picked chip disappears and each new
  // pick replaces the prior one instead of appending. Callers that DO pass
  // `value` still take precedence (controlled semantic preserved).
  const [localMultiSelected, setLocalMultiSelected] = React.useState<BillingAccount[]>([]);
  // Internal selection for the checkbox-list variant (this variant is
  // uncontrolled — callers just receive the resolved BillingAccount[] via
  // onSelected).
  const [checkboxSelection, setCheckboxSelection] = React.useState<BillingAccount[]>([]);
  const hasHydratedRef = React.useRef(false);

  const { data: response, isFetching } = useBillingAccounts({ status, type, caller });

  const sortAndFilter = React.useCallback(
    (rows: BillingAccount[], filter: BusinessTypeFilter) => {
      const sorted = [...rows].sort((a, b) => {
        const aRank = STATUS_ORDER[a.status] ?? 99;
        const bRank = STATUS_ORDER[b.status] ?? 99;
        if (aRank !== bRank) return aRank - bRank;
        return (a.companyName ?? '').localeCompare(b.companyName ?? '');
      });
      if (filter === 'all') return sorted;
      return sorted.filter((row) => row.businessType === filter);
    },
    []
  );

  const handleOpen = () => {
    setOpen(true);
    if (response?.data?.data) {
      setOptions(sortAndFilter(response.data.data, businessTypeFilter));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleBusinessTypeChange = (event: SelectChangeEvent) => {
    const next = event.target.value as BusinessTypeFilter;
    setBusinessTypeFilter(next);
    onBusinessTypeChange?.(next);
    if (response?.data?.data) {
      setOptions(sortAndFilter(response.data.data, next));
    }
  };

  const allLoadedOptions = React.useMemo(
    () => sortAndFilter(response?.data?.data ?? [], businessTypeFilter),
    [response, businessTypeFilter, sortAndFilter]
  );

  // One-shot hydration for the checkbox-list variant: as soon as options
  // arrive, resolve `defaultSelectAll` / `preSelectedLocationIds` to
  // BillingAccount objects and emit them via onSelected. Further changes
  // come from the user, so we hydrate exactly once.
  React.useEffect(() => {
    if (!isCheckboxList || !isMultiple) return;
    if (hasHydratedRef.current) return;
    if (allLoadedOptions.length === 0) return;

    let initial: BillingAccount[] = [];
    if (defaultSelectAll) {
      initial = [...allLoadedOptions];
    } else if (preSelectedLocationIds && preSelectedLocationIds.length > 0) {
      const idSet = new Set(preSelectedLocationIds);
      initial = allLoadedOptions.filter((o) => o.locationId && idSet.has(o.locationId));
    }
    hasHydratedRef.current = true;
    setCheckboxSelection(initial);
    if (initial.length > 0) {
      (props as MultiProps).onSelected?.(initial);
    }
  }, [
    isCheckboxList,
    isMultiple,
    allLoadedOptions,
    defaultSelectAll,
    preSelectedLocationIds,
    props
  ]);

  // Normalise value/onChange so MUI Autocomplete gets the right shape
  // regardless of single/multi mode. Both modes fall back to internal state
  // when the caller doesn't supply a `value` prop (uncontrolled semantic).
  const autocompleteValue = isMultiple
    ? ((props as MultiProps).value ?? localMultiSelected)
    : ((props as SingleProps).value ?? localSelected);
  const handleChange = (_e: unknown, newValue: BillingAccount | BillingAccount[] | null) => {
    if (isMultiple) {
      const arr = (newValue as BillingAccount[]) ?? [];
      setSelectedCount(arr.length);
      setLocalMultiSelected(arr);
      (props as MultiProps).onSelected?.(arr);
    } else {
      const single = newValue as BillingAccount | null;
      setLocalSelected(single);
      (props as SingleProps).onSelected?.(single);
    }
  };

  const businessTypeDropdown =
    showBusinessTypeFilter && !lockBusinessType ? (
      <Select
        value={businessTypeFilter}
        onChange={handleBusinessTypeChange}
        size="small"
        sx={{ minWidth: 130, height: 40 }}
        displayEmpty>
        {BUSINESS_TYPE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    ) : null;

  const goHref = !isMultiple ? (props as SingleProps).goHref : undefined;

  const autocomplete = (
    <Autocomplete
      sx={{ width, flex: showBusinessTypeFilter || goHref || isMultiple ? 1 : undefined }}
      inputMode="search"
      open={open}
      size="small"
      multiple={isMultiple as any}
      onOpen={handleOpen}
      onClose={handleClose}
      isOptionEqualToValue={(option, candidate) => option.id === candidate.id}
      getOptionLabel={(option) => option.companyName ?? ''}
      options={options as BillingAccount[]}
      loading={isFetching}
      value={autocompleteValue as any}
      onChange={handleChange as any}
      filterSelectedOptions={isMultiple}
      groupBy={(option) => option.status ?? 'unknown'}
      renderGroup={(params) => {
        const meta = STATUS_COLORS[params.group] ?? {
          label: params.group,
          color: '#374151',
          bg: '#E5E7EB'
        };
        return (
          <li key={params.key}>
            <Box
              sx={{
                position: 'sticky',
                top: -8,
                zIndex: 1,
                px: 2,
                py: 0.75,
                bgcolor: meta.bg,
                borderLeft: `3px solid ${meta.color}`
              }}>
              <Typography variant="caption" sx={{ color: meta.color, fontWeight: 700, letterSpacing: 0.5 }}>
                {meta.label.toUpperCase()}
              </Typography>
            </Box>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        );
      }}
      renderOption={(liProps, option) => {
        const isCore = !!option.locationId;
        const isNegotiator = !!option.reapitCompanyId;
        const statusMeta = STATUS_COLORS[option.status] ?? null;
        const circleBadgeSx = {
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 700,
          flexShrink: 0
        } as const;
        return (
          <li {...liProps} key={option.id}>
            <Stack direction="row" alignItems="center" spacing={1} width="100%">
              {statusMeta && (
                <Box
                  aria-label={statusMeta.label}
                  title={statusMeta.label}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusMeta.color,
                    flexShrink: 0
                  }}
                />
              )}
              <span style={{ flex: 1 }}>{option.companyName}</span>
              {isCore && (
                <Box
                  sx={{
                    ...circleBadgeSx,
                    bgcolor: '#DBEAFE',
                    color: '#1E40AF',
                    border: '1px solid #1E40AF'
                  }}>
                  C
                </Box>
              )}
              {isNegotiator && (
                <Box
                  sx={{
                    ...circleBadgeSx,
                    bgcolor: '#FCE7F3',
                    color: '#9D174D',
                    border: '1px solid #9D174D'
                  }}>
                  N
                </Box>
              )}
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={title}
          required={required}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
        />
      )}
    />
  );

  // ── Checkbox-list variant ────────────────────────────────────────────
  // MUI Select with checkboxes + Select-All row. Used by callers that
  // want the "sync all / pick some" UX. Returns BillingAccount[] via
  // onSelected (same contract as the search-variant multi mode).
  if (isCheckboxList) {
    const isAllSelected =
      allLoadedOptions.length > 0 && checkboxSelection.length === allLoadedOptions.length;
    const selectedLocationIds = checkboxSelection
      .map((a) => a.locationId)
      .filter((v): v is string => !!v);

    const handleCheckboxChange = (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as string[] | string;
      // "all" toggle row
      if (Array.isArray(value) ? value.includes('all') : value === 'all') {
        const next = isAllSelected ? [] : [...allLoadedOptions];
        setCheckboxSelection(next);
        (props as MultiProps).onSelected?.(next);
        return;
      }
      const ids = Array.isArray(value) ? value : [value];
      const idSet = new Set(ids);
      const next = allLoadedOptions.filter((a) => a.locationId && idSet.has(a.locationId));
      setCheckboxSelection(next);
      (props as MultiProps).onSelected?.(next);
    };

    return (
      <Stack sx={{ width: width === 320 ? 250 : width }}>
        {isFetching && <LinearProgress color="primary" style={{ width: '100%', top: 0 }} />}
        <Select
          multiple
          fullWidth
          size="small"
          value={selectedLocationIds}
          onChange={handleCheckboxChange}
          sx={{ backgroundColor: '#ffffff', fontSize: 12 }}>
          <MenuItem value="all" style={{ backgroundColor: isAllSelected ? '#e0e0e0' : 'inherit' }}>
            <Checkbox
              checked={isAllSelected}
              sx={{ padding: 0, marginRight: 1 }}
              indeterminate={
                checkboxSelection.length > 0 && checkboxSelection.length < allLoadedOptions.length
              }
            />
            Select All
          </MenuItem>
          {allLoadedOptions.map((account) => {
            const checked = account.locationId
              ? selectedLocationIds.includes(account.locationId)
              : false;
            return (
              <MenuItem
                key={account.locationId ?? account.id}
                value={account.locationId ?? ''}
                style={{ backgroundColor: checked ? '#e0e0e0' : 'inherit' }}>
                <Checkbox sx={{ padding: 0, marginRight: 1 }} checked={checked} />
                {account.companyName}
              </MenuItem>
            );
          })}
        </Select>
      </Stack>
    );
  }

  // No extras → return the plain Autocomplete so existing minimal callers
  // (e.g. the bills filter) stay layout-identical.
  if (!businessTypeDropdown && !goHref && !isMultiple) {
    return autocomplete;
  }

  const singleSelected = (autocompleteValue as BillingAccount | null) ?? null;

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
      {businessTypeDropdown}
      {autocomplete}
      {isMultiple && selectedCount > 0 && (
        <Chip
          label={`${selectedCount} selected`}
          size="small"
          color="primary"
          sx={{ fontWeight: 600, minWidth: 'fit-content' }}
        />
      )}
      {goHref && singleSelected && (
        <Button
          component="a"
          href={goHref(singleSelected)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Go
        </Button>
      )}
    </Stack>
  );
}
